import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  NotePencil, Plus, MagnifyingGlass, ChartBar, Sparkle, CaretRight, Paperclip, ArrowSquareUpRight,
  Fire, Trophy, Check, Lightbulb, Trash, Printer,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthContext';
import { engApi, REPORT_DOC_LABEL, REPORT_DOC_BADGE } from './engApi';
import { openPrintWindow } from '@/lib/printWindow';
import { buildReportPrintHtml } from './engReportPrint';
import { formatShort } from '@/utils/datetime';
import { todayJst } from '@/utils/jstDate';

const fmtDT = (s) => (s ? formatShort(s) : '—');

const DOW = ['月', '火', '水', '木', '金', '土', '日'];

function streakMessage(s) {
  if (s.today_submitted) return '本日提出済み！その調子です 🎉';
  if (s.current_streak >= 5) return `${s.current_streak}日連続中 — 今日の日報で記録を伸ばしましょう 🔥`;
  if (s.current_streak > 0) return '連続記録が続いています — 今日も忘れずに';
  return '今日の日報から新しい連続記録を始めましょう';
}

function StreakStrip({ stats }) {
  if (!stats) return null;
  return (
    <div className="rounded-xl border border-teal-100 bg-zinc-50 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="report-streak-strip">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${stats.current_streak > 0 ? 'bg-orange-100 text-orange-500' : 'bg-zinc-100 text-zinc-400'}`}>
          <Fire size={18} weight="duotone" />
        </span>
        <div>
          <p className="text-[15px] font-bold text-zinc-900 leading-none" data-testid="streak-current">
            {stats.current_streak}<span className="text-[11px] font-semibold text-zinc-500 ml-0.5">日連続</span>
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5 flex items-center gap-0.5">
            <Trophy size={10} weight="duotone" className="text-amber-500" /> 自己ベスト {stats.best_streak}日
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1" data-testid="streak-week-dots">
        {stats.week.days.map((d) => (
          <div key={d.date} className="flex flex-col items-center gap-0.5">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold border ${
              d.submitted ? 'bg-teal-500 border-teal-500 text-white'
                : d.is_future ? 'bg-white border-zinc-200 text-zinc-300'
                  : d.weekday > 4 ? 'bg-zinc-50 border-zinc-100 text-zinc-300'
                    : 'bg-white border-zinc-300 text-zinc-400'}`}>
              {d.submitted ? <Check size={11} weight="bold" /> : DOW[d.weekday]}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 text-[12px] text-zinc-600">
        <span data-testid="streak-week-count">今週 <b className="text-zinc-900">{stats.week.count}</b> 件</span>
        <span className="inline-flex items-center gap-1" data-testid="streak-week-lessons">
          <Lightbulb size={12} weight="duotone" className="text-violet-500" /> 学び <b className="text-zinc-900">{stats.week.lessons}</b> 件
        </span>
        <span className="hidden sm:inline" data-testid="streak-total">累計 <b className="text-zinc-900">{stats.total_reports}</b> 件</span>
      </div>
      <p className="text-[12px] font-semibold text-teal-700 ml-auto" data-testid="streak-message">{streakMessage(stats)}</p>
    </div>
  );
}

export default function EngReports() {
  const { user } = useAuth();
  const { access } = useOutletContext() || {};
  const navigate = useNavigate();
  const isMgr = ['admin', 'manager'].includes(user?.role);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ projects: [], departments: [], members: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fEngineer, setFEngineer] = useState('all');
  const [fProject, setFProject] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fDept, setFDept] = useState('all');
  // 既定は今月だけ。過去の月・全期間は選び直せる（既定を変えただけで、
  // 閲覧できる範囲は狭めない — 締めた月の見直しや月次比較のため）。
  const [fMonth, setFMonth] = useState(() => todayJst().slice(0, 7));
  const [fRange, setFRange] = useState('month');

  useEffect(() => { engApi.reportsMeta().then(setMeta).catch(() => {}); }, []);
  const isEngMember = !!(access?.is_member || access?.is_admin);
  useEffect(() => {
    if (isEngMember) engApi.myReportStats().then(setStats).catch(() => {});
  }, [isEngMember]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (fEngineer !== 'all') params.engineer_id = fEngineer;
    if (fProject !== 'all') params.project_id = fProject;
    if (fStatus !== 'all') params.report_status = fStatus;
    if (fRange === 'month' && /^\d{4}-\d{2}$/.test(fMonth)) {
      const [y, m] = fMonth.split('-').map(Number);
      params.date_from = `${fMonth}-01`;
      params.date_to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    }
    engApi.reports(params).then(setReports)
      .catch(() => toast.error('日報の読み込みに失敗しました'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [fEngineer, fProject, fStatus, fRange, fMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // 検索は入力しながら反映（ナレッジ検索として使うため Enter 不要）
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const projOptions = useMemo(() => (fDept === 'all'
    ? meta.projects
    : meta.projects.filter((p) => p.department_id === fDept)), [meta.projects, fDept]);

  const shown = useMemo(() => reports, [reports]);

  const doDelete = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`${r.report_date} の日報を削除しますか？\nこの操作は取り消せません。`)) return;
    try {
      await engApi.deleteReport(r.id);
      toast.success('日報を削除しました');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const doPrint = (r, e) => {
    e.stopPropagation();
    engApi.report(r.id).then((full) => openPrintWindow(buildReportPrintHtml(full)))
      .catch(() => toast.error('印刷用データの取得に失敗しました'));
  };

  return (
    <div className="space-y-4" data-testid="eng-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <NotePencil size={24} weight="duotone" className="text-teal-600" /> 日報
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">日々の技術活動を数分で記録 — 週次のAIダイジェストで自動集約されます</p>
        </div>
        <div className="flex items-center gap-2">
          {isMgr && (
            <>
              <Button size="sm" variant="outline" asChild className="h-9" data-testid="reports-dashboard-link">
                <Link to="/engineering/reports/dashboard"><ChartBar size={14} className="mr-1" /> ダッシュボード</Link>
              </Button>
              <Button size="sm" variant="outline" asChild className="h-9" data-testid="reports-digests-link">
                <Link to="/engineering/reports/digests"><Sparkle size={14} className="mr-1" /> 週間ダイジェスト</Link>
              </Button>
              <Button size="sm" variant="outline" asChild className="h-9 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="reports-kb-export-link">
                <Link to="/engineering/kb-export"><ArrowSquareUpRight size={14} className="mr-1" /> 週次更新をナレッジへ出力</Link>
              </Button>
            </>
          )}
          <Button size="sm" asChild className="h-9 bg-teal-700 hover:bg-teal-800" data-testid="report-create-btn">
            <Link to="/engineering/reports/new"><Plus size={14} className="mr-1" /> 日報を作成</Link>
          </Button>
        </div>
      </div>

      <StreakStrip stats={stats} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="作業対象・目標・作業内容・課題・技術的根拠・教訓を検索…" className="pl-8 h-9" data-testid="reports-search-input" />
        </div>
        <Select value={fEngineer} onValueChange={setFEngineer}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="reports-filter-engineer"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全エンジニア</SelectItem>
            {meta.members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fDept} onValueChange={(v) => { setFDept(v); setFProject('all'); }}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="reports-filter-dept"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部署</SelectItem>
            {meta.departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fProject} onValueChange={setFProject}>
          <SelectTrigger className="h-9 w-[190px]" data-testid="reports-filter-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全プロジェクト</SelectItem>
            {projOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="reports-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべての日報</SelectItem>
            {Object.entries(REPORT_DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fRange} onValueChange={setFRange}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="reports-filter-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">月ごと</SelectItem>
            <SelectItem value="all">全期間</SelectItem>
          </SelectContent>
        </Select>
        {fRange === 'month' && (
          <Input type="month" value={fMonth} onChange={(e) => setFMonth(e.target.value)}
            className="h-9 w-[150px]" data-testid="reports-filter-month" />
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-zinc-200 px-5 py-14 text-center">
          <div className="w-7 h-7 mx-auto rounded-full border-4 border-zinc-200 border-t-teal-600 animate-spin" />
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 px-5 py-14 text-center" data-testid="reports-empty">
          <NotePencil size={32} weight="duotone" className="mx-auto text-zinc-300" />
          {/* 「まだ無い」と「この月には無い」は別の話。既定が今月なので、
              月を絞った結果の空を「まだ無い」と言うと過去分が消えたように見える。 */}
          <p className="mt-2 text-sm text-zinc-500">
            {fRange === 'month' ? `${fMonth} の日報はありません` : '日報がまだありません'}
          </p>
          {fRange === 'month' && (
            <button type="button" onClick={() => setFRange('all')}
              className="mt-1 text-xs font-semibold text-teal-700 underline hover:text-teal-900"
              data-testid="reports-empty-show-all">全期間を表示する</button>
          )}
          <Button size="sm" asChild className="mt-3 bg-teal-700 hover:bg-teal-800">
            <Link to="/engineering/reports/new"><Plus size={14} className="mr-1" /> 最初の日報を作成</Link>
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-[110px_1fr_1.4fr_180px_130px_86px] gap-3 px-4 py-2.5 text-[11px] font-bold text-zinc-400 uppercase tracking-wider border-b border-zinc-200">
            <span>日付</span><span>担当者</span><span>作業対象</span><span>タグ</span><span>更新日時</span><span className="text-right">操作</span>
          </div>
          <div className="divide-y divide-zinc-200">
            {shown.map((r) => {
              const isMine = r.engineer_id === user?.id;
              return (
                <div key={r.id} className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors grid md:grid-cols-[110px_1fr_1.4fr_180px_130px_86px] gap-x-3 gap-y-1 items-center cursor-pointer"
                  onClick={() => navigate(`/engineering/reports/${r.id}`)}
                  data-testid={`report-row-${r.id}`}>
                  <span className="text-[13px] font-semibold text-zinc-800">{r.report_date}</span>
                  <span className="text-[13px] text-zinc-700 truncate">{r.engineer_name}</span>
                  <span className="text-[13px] text-zinc-600 truncate flex items-center gap-1">
                    {r.project_name || r.display_title
                      || <span className="text-[11px] text-teal-600 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">一般作業</span>}
                    {(r.attachments || []).length > 0 && <Paperclip size={12} className="text-zinc-400 shrink-0" />}
                  </span>
                  <span className="flex items-center gap-1 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${REPORT_DOC_BADGE[r.report_status] || ''}`}
                      data-testid={`report-tag-doc-${r.id}`}>
                      {REPORT_DOC_LABEL[r.report_status] || r.report_status}
                    </span>
                    {r.generated_by_ai && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border font-semibold bg-violet-50 text-violet-700 border-violet-200"
                        title={`モデル: ${r.ai_model_name || '—'}`} data-testid={`report-tag-ai-${r.id}`}>AI生成</span>
                    )}
                    {r.has_translation_en && (
                      <span className="text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                        title="英訳プレビューあり" data-testid={`report-en-badge-${r.id}`}>EN</span>
                    )}
                  </span>
                  <span className="text-[11px] text-zinc-400">{fmtDT(r.updated_at)}</span>
                  <span className="flex items-center justify-end gap-0.5">
                    <button type="button" onClick={(e) => doPrint(r, e)} className="p-1.5 rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-800" title="印刷 / PDF" data-testid={`report-print-${r.id}`}>
                      <Printer size={14} />
                    </button>
                    {(isMine || user?.role === 'admin') && (
                      <button type="button" onClick={(e) => doDelete(r, e)} className="p-1.5 rounded text-rose-500 hover:bg-rose-50" title="削除" data-testid={`report-delete-${r.id}`}>
                        <Trash size={14} />
                      </button>
                    )}
                    <CaretRight size={13} className="text-zinc-300 ml-1 hidden md:block" />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
